import { registryTemplateCacheKey } from '#~/device/registry/registry-config-cache.js';
/**
 * E2E renderer test probe — lets Playwright push a proto calibration
 * `Status` for a device id straight into the depot via
 * `globalThis.__irisSetCalibrationStatus`.
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive. The
 * `<Calibration>` watchdog (routes/room/watchdog/calibration.tsx) drives
 * `calibration_completed` off `device.control.calibration.status` changes
 * on the room in React Context; e2e cannot reach that context or real PTZ
 * hardware, so this probe writes into the SAME depot path production sync
 * writes to (`room/provider/utils.ts:syncDevices`), reusing the watchdog's
 * diff loop unmodified instead of re-deriving it in test code.
 *
 * The injected device's `control.config` is a throwaway HTTP config — no
 * real protocol connection is ever attempted for an e2e-only status probe,
 * so `host`/`port` carry no meaning beyond satisfying `Control.State`.
 *
 * Room resolution reads the ACTIVE `roomId` off the URL (`/room/:roomId`),
 * matching the same route param React Router threads into
 * `<Provider roomId={...}>`, so it always targets the room the mounted
 * `<Calibration>` watchdog is subscribed to.
 *
 * ENG-4401 CI 30104262728 (attempts 1+2, win): the early-return branches used
 * to be SILENT (`return;` on no-room-id / room-not-in-depot). When the room
 * route's Guard regressed between the receipt's one-shot ready gate and the
 * injection (ENG-4154 agent-status flap), every injected status vanished with
 * zero diagnostic and the receipt died at its observation window. Each write
 * now returns a `CalibrationProbeWriteReceipt` so the caller can distinguish
 * "wrote into the live room" from "silently dropped" and re-attempt against a
 * settled app. The depot write path itself is byte-identical to before.
 */
import { castId } from '@studio/models';
import { Protocol } from '@studio/models/control';
import { Status } from '@studio/models/control/calibration';
import { DeviceDefaults } from '@studio/models/device';
import type { Brand, Model } from '@studio/models/device';
import type { Agent } from '@studio/models/agent';
// Static barrel import, as in device-probe.ts. The barrel pulls Vite `?worker`
// modules jest cannot resolve, so tests importing this module mock
// `#~/agent/index.js` and import it dynamically (calibration-probe.test.ts).
// See knowledge: esm-jest-mock-patterns (ai.knowledge/global).
import { locateProxy } from '#~/agent/index.js';
import type { Device } from '#~/device/index.js';
import { depot } from '#~/depot/index.js';
import type { Producer } from '#~/depot/index.js';
import type { Room } from '#~/room/index.js';

/**
 * Outcome of one probe write. `written: false` carries the reason and the
 * observed pathname so the e2e harness can log WHY an injection was dropped
 * (the pre-receipt probe lost this information, which cost a full CI
 * investigation to reconstruct — see the header note).
 */
export type CalibrationProbeWriteReceipt =
  | { readonly written: true; readonly roomId: string }
  | {
      readonly written: false;
      readonly reason: 'no-room-id' | 'room-not-in-depot';
      readonly pathname: string;
    };

export interface CalibrationProbeCatalog {
  modelId: string;
  brandId: string;
  modelName: string;
  brandName: string;
}

interface CalibrationProbe {
  setStatus(
    deviceId: string,
    status: Status,
    catalog?: CalibrationProbeCatalog
  ): CalibrationProbeWriteReceipt;
}

/**
 * Renderer-process gate. `process.env` is NOT populated in this renderer —
 * even with `nodeIntegration: true` + `contextIsolation: false`
 * (studio/electron/src/main/studio/window.ts), Electron does not forward the
 * OS environment into the loaded page's `process.env` (confirmed empirically:
 * `process` exists but `process.env` has zero keys). The preload script
 * (studio/electron/src/preload/index.ts, via `@electron-toolkit/preload`'s
 * `electronAPI`) DOES run with a real inherited `process.env` and exposes it
 * on `window.electron.process.env` — the same bridge `posthog/manager.ts`'s
 * `readE2EFeatureFlagOverrides` already reads IRIS_E2E_TEST through. This was
 * previously read via bare `process.env.IRIS_E2E_TEST`, which always
 * evaluated to `undefined` in the packaged app, so this probe silently never
 * activated (ENG-4281 CI investigation, 2026-07-23).
 */
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

// Matches the `/room/:roomId` and `/room/:roomId/boot` routes (see
// `routes/room/boot-readiness.test.tsx` for the canonical route shape).
const ACTIVE_ROOM_PATH = /^\/room\/([^/]+)/;

const resolveActivePathname = (): string =>
  typeof window === 'undefined' ? '' : window.location.pathname;

const resolveActiveRoomId = (pathname: string): string | undefined =>
  ACTIVE_ROOM_PATH.exec(pathname)?.[1];

/**
 * Build the probe's `setStatus` against an injected producer accessor.
 * Factored out of the install branch so the receipt contract (which the e2e
 * calibration receipt's injection loop now depends on) is unit-testable with
 * a fake depot — the install side effect below stays IRIS_E2E_TEST-gated.
 *
 * Write (or synthesize) a device's calibration status directly into the
 * currently-displayed room (resolved from the URL). If the device id is not
 * yet present, a minimal control-capable device is created so the watchdog's
 * `isControlDevice` check passes; `calibration-lifecycle.ts`'s device-context
 * resolver degrades gracefully to `{ device_id }` when brand/model/studio
 * context cannot be resolved, so no registry data is required here.
 */
export const createCalibrationSetStatus =
  (getProduce: () => Producer) =>
  (
    deviceId: string,
    status: Status,
    catalog?: CalibrationProbeCatalog
  ): CalibrationProbeWriteReceipt => {
    const pathname = resolveActivePathname();
    const roomId = resolveActiveRoomId(pathname);
    if (!roomId) {
      return { written: false, reason: 'no-room-id', pathname };
    }
    // The room-present check must live INSIDE the same produce() as the write:
    // checking depot state before a separate produce() would reopen exactly the
    // check-then-act gap this receipt exists to close.
    let written = false;
    getProduce()((draft) => {
      const room = draft.rooms[castId<Room['id']>(roomId)];
      if (!room) {
        return;
      }
      // Populate the same catalog the production metadata resolver reads. This
      // test-only input never calls analytics or supplies an expected event.
      if (catalog !== undefined) {
        const brandId = castId<Brand['id']>(catalog.brandId);
        const modelId = castId<Model['id']>(catalog.modelId);
        const brand: Brand = { id: brandId, name: catalog.brandName };
        const model: Model = {
          id: modelId,
          brandId,
          name: catalog.modelName,
          fidelity: 0,
          iconUrl: '',
          generic: false
        };
        draft.registry.brands ??= { generic: brand, items: {} };
        draft.registry.brands.items[brandId] = brand;
        draft.registry.templates[registryTemplateCacheKey(modelId)] = { model };
      }
      const id = castId<Device['id']>(deviceId);
      const existing = room.devices[id];
      const device: Device = {
        ...DeviceDefaults,
        ...existing,
        id,
        ...(catalog !== undefined ? { modelId: castId<Model['id']>(catalog.modelId) } : {}),
        agentId: existing?.agentId ?? castId<Agent['id']>('e2e-calibration-fixture-agent'),
        control: {
          ...existing?.control,
          config: existing?.control?.config ?? {
            protocol: Protocol.HTTP,
            host: 'iris-e2e-fixture.local',
            port: 0
          },
          calibration: { status }
        }
      };
      room.devices[id] = device;
      written = true;
    });
    return written
      ? { written: true, roomId }
      : { written: false, reason: 'room-not-in-depot', pathname };
  };

// ─── CALIBRATION HISTORY RECORDER (read side) ──────────────────────
//
// The write side above pushes a status INTO the depot for the watchdog receipt.
// This read side observes the calibration state the ENCODER publishes for a
// device, so a HIL spec can assert the encoder never flashes a false NULL when a
// device is (re)added: the recorded sequence must never contain a
// NULL -> OK/OK_GENERIC/SUCCESS step without RUNNING in between (a device cannot
// become calibrated without a calibration run).
//
// It reads the RAW agent-proxy status (proxy.state.status.device[id].calibration
// .status), NOT the JS-normalised control.calibration. pbToJsControlCalibration
// collapses CALIBRATION_STATE_NULL AND absent to `undefined` (protobuf/control.ts),
// so the normalised view cannot tell "encoder published NULL" (the bug) from "no
// calibration published yet" (the benign pre-snapshot window). The raw status
// object exists ONLY once the encoder has published a calibration status, and its
// `state` field is NULL (0, elided on the wire) vs a real OK — the distinction the
// invariant needs. Per rule think-in-systems (ai.knowledge/global): a primitive
// that reuses the same proxy locator device-probe.ts reads through.

const calibrationStateHistory = new Map<string, Status[]>();

/**
 * Fold the current agent-proxy snapshot into each depot device's recorded
 * calibration sequence. Appends a device's state only when it CHANGES from the
 * last recorded value (consecutive duplicates collapsed), so the sequence is the
 * ordered set of distinct states the encoder drove the device through.
 */
const sampleCalibrationStates = (): void => {
  const { rooms } = depot.getState();
  for (const room of Object.values(rooms)) {
    for (const deviceId of Object.keys(room.devices)) {
      const proxy = locateProxy(castId<Device['id']>(deviceId));
      const calibrationStatus = proxy?.state.status?.device[deviceId]?.calibration?.status;
      // No status object => the encoder has not published a calibration state yet
      // (the pre-snapshot window). Skip so it is not mistaken for a published NULL.
      if (!calibrationStatus) {
        continue;
      }
      // A published NULL elides `state` (proto3 zero value), so absent === NULL.
      const state = calibrationStatus.state ?? Status.CALIBRATION_STATE_NULL;
      const key = deviceId.toLowerCase();
      const seq = calibrationStateHistory.get(key) ?? [];
      if (seq.length === 0 || seq[seq.length - 1] !== state) {
        seq.push(state);
        calibrationStateHistory.set(key, seq);
      }
    }
  }
};

/**
 * Readback for the HIL spec: the ordered distinct calibration states the encoder
 * published for a device, as raw StatusCalibrationState enum values. Empty when
 * nothing was recorded for the id. Case-insensitive on deviceId so a spec can
 * pass the tray row's prog-key id verbatim.
 */
export const getCalibrationHistory = (deviceId: string): number[] => [
  ...(calibrationStateHistory.get(deviceId.toLowerCase()) ?? [])
];

if (isE2eEnabled()) {
  const probe: CalibrationProbe = {
    // Read `produce` per call (not once at install) — identical to the
    // pre-receipt probe's `depot.getState().produce(...)` timing.
    setStatus: createCalibrationSetStatus(() => depot.getState().produce)
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisSetCalibrationStatus = probe.setStatus;

  // Read side: record each device's encoder-published calibration transitions.
  // Sampling on every depot change catches both the add-time snapshot and the
  // ~1s status tick, which drive depot writes off the same proxy. Subscribe
  // only — at module-eval time the depot is still empty.
  depot.subscribe(() => sampleCalibrationStates());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisGetCalibrationHistory = getCalibrationHistory;
}
